import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

type MessageKey =
  | 'successSaveNotification'
  | 'successAlreadySavedNotification'
  | 'errorMismatchedNotification'
  | 'errorMismatchedNotificationForms'
  | 'errorMetadataTemplateNotification'
  | 'errorUnsupportedCharactersNotificationForms'
  | 'errorUnsupportedCharactersNotificationFileRequest'
  | 'clearMetadataTemplateButton'
  | 'inactiveNotification'
  | 'successCopyNotification'
  | 'showFolderButton'
  | 'errorMaxFieldsNotification'
  | 'unknownOnSaveError';

const messages: Record<MessageKey, MessageDescriptor> = defineMessages({
  successSaveNotification: {
    id: 'boxFileRequest.builder.successSaveNotification',
    description: 'Notification message on successful save',
    defaultMessage: 'Your changes have been saved.',
  },
  successAlreadySavedNotification: {
    id: 'boxFileRequest.builder.successAlreadySavedNotification',
    description: 'Notification message on click save when no changes were made',
    defaultMessage: 'Form is already saved.',
  },
  errorMismatchedNotification: {
    id: 'boxFileRequest.builder.errorMismatchedNotification',
    description: 'Notification message on save 409 and data refresh',
    defaultMessage:
      'Your changes could not be saved as another collaborator has updated this File Request while you were editing it. Please see their changes and try again.',
  },
  errorMismatchedNotificationForms: {
    id: 'boxFileRequest.builder.errorMismatchedNotificationForms',
    description: 'Notification message on save 409 and data refresh for Forms',
    defaultMessage:
      'Your changes could not be saved as another collaborator has updated this Form while you were editing it. Please see their changes and try again.',
  },
  errorMetadataTemplateNotification: {
    id: 'boxFileRequest.builder.errorMetadataTemplateNotification',
    description: 'Notification message on load metadata template error',
    defaultMessage:
      'This metadata template can no longer be used by your enterprise. Click here to clear the metadata fields and save your changes to continue.',
  },
  errorUnsupportedCharactersNotificationForms: {
    id: 'boxFileRequest.builder.errorUnsuppportedCharactersNotificationForms',
    description:
      'Notification message when form contains unsupported characters on save',
    defaultMessage: 'Form contains unsupported characters.',
  },
  errorUnsupportedCharactersNotificationFileRequest: {
    id: 'boxFileRequest.builder.errorUnsuppportedCharactersNotificationFileRequest',
    description:
      'Notification message when file request contains unsupported characters on save',
    defaultMessage: 'File request contains unsupported characters.',
  },
  clearMetadataTemplateButton: {
    id: 'boxFileRequest.builder.clearMetadataTemplateButton',
    description: 'Clear button to clear metadata template on load error',
    defaultMessage: 'Clear',
  },
  inactiveNotification: {
    id: 'boxFileRequest.builder.inactiveNotification',
    description:
      'Notification message when file request or form has been deactivated due to invalid metadata',
    defaultMessage: `{type, select,
      form {This form has been deactivated. Please resolve any issues with this form and save your changes to continue.}
      other {This file request has been deactivated. Please resolve any issues with this file request and save your changes to continue.}
      }`,
  },
  successCopyNotification: {
    id: 'boxFileRequest.builder.successCopyNotification',
    description:
      'Notification message on successful copy of file request. Single show the folderName, plural shows the count.',
    defaultMessage: `The file request was successfully copied to {count, plural,
      one {{folderName}}
      other {{count}+ folders}
    }`,
  },
  showFolderButton: {
    id: 'boxFileRequest.builder.showFolderButton',
    description:
      'Show folder button on notification message on successful copy of file request',
    defaultMessage: 'Show Folder',
  },
  errorMaxFieldsNotification: {
    id: 'boxFileRequest.builder.errorMaxFieldsNotification',
    description:
      'Notification message on max fields exceeded. maxCount is the max number of fields allowed.',
    defaultMessage: `Failed to add field(s) because it would exceed the maximum number of fields ({maxCount})`,
  },
  unknownOnSaveError: {
    id: 'boxFileRequest.builder.unknownOnSaveError',
    description:
      'Notification message when an unknown error occured after saving a Form.',
    defaultMessage: `{type, select,
      form {Unable to save form because of an unknown error.}
      other {Unable to save file request because of an unknown error.}
    }`,
  },
});

export default messages;
